import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { AlertTriangle, Trash2, RefreshCw, Users, ChevronDown, ChevronRight } from "lucide-react";

/**
 * SA UI za čiščenje pomotoma uvoženih članov (npr. LZS registry v napačno LD).
 * Prikaže:
 *  - seznam po (LD, datum vstopa) skupinah,
 *  - duplikate po stevilka_clana med LD-ji (kritični).
 * SA lahko z enim klikom izbriše celo skupino ali izbrane user_id-je.
 */
const SuperAdminCleanup = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ summary: [], duplicates_by_stevilka: {} });
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [busy, setBusy] = useState(false);

  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sa/users/orphans`, { headers });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Napaka pri nalaganju");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  const bulkDelete = async (userIds, ldId, label) => {
    if (!window.confirm(`Izbriši ${userIds.length} članov iz LD "${ldId}"?\n\n${label}\n\nTega ni mogoče razveljaviti.`)) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/sa/users/bulk-delete`, { user_ids: userIds, ld_id: ldId }, { headers });
      toast.success(`Izbrisanih: ${res.data.deleted}`);
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Napaka pri brisanju");
    } finally {
      setBusy(false);
    }
  };

  const dupList = Object.entries(data.duplicates_by_stevilka || {});

  // Group summary by ld_id
  const byLd = {};
  (data.summary || []).forEach(row => {
    if (!byLd[row.ld_id]) byLd[row.ld_id] = [];
    byLd[row.ld_id].push(row);
  });

  return (
    <div className="space-y-6" data-testid="sa-cleanup">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-amber-600" />
            Čiščenje članov
          </h2>
          <p className="text-sm text-muted-foreground">
            Odkrivanje in odstranitev napačno uvoženih članov (cross-LD leaki).
          </p>
        </div>
        <Button variant="outline" onClick={fetchData} disabled={loading} data-testid="sa-cleanup-refresh">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Osveži
        </Button>
      </div>

      {/* Duplikati po stevilka_clana med LD-ji */}
      {dupList.length > 0 && (
        <Card className="border-red-300">
          <CardHeader>
            <CardTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Kritično: duplikati po številki člana ({dupList.length})
            </CardTitle>
            <CardDescription>
              Ista <b>stevilka_clana</b> se pojavlja v več zapisih (možni cross-LD podvojki).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="max-h-64 overflow-y-auto space-y-1">
              {dupList.slice(0, 50).map(([stevilka, ids]) => (
                <div key={stevilka} className="flex items-center justify-between text-sm border-b py-1">
                  <span className="font-mono">{stevilka}</span>
                  <Badge variant="destructive">{ids.length}× zapis</Badge>
                </div>
              ))}
              {dupList.length > 50 && (
                <p className="text-xs text-muted-foreground pt-2">... in še {dupList.length - 50}</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Seznam po LD-jih in datumih */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><div className="spinner" /></div>
      ) : (
        <div className="space-y-3">
          {Object.entries(byLd).map(([ldId, groups]) => {
            const total = groups.reduce((s, g) => s + g.count, 0);
            const isOpen = expandedGroup === ldId;
            return (
              <Card key={ldId} data-testid={`sa-cleanup-ld-${ldId}`}>
                <CardHeader
                  className="cursor-pointer hover:bg-muted/40"
                  onClick={() => setExpandedGroup(isOpen ? null : ldId)}
                >
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      <Users className="h-4 w-4" />
                      LD: <span className="font-mono">{ldId}</span>
                      <Badge variant="secondary">{total} članov</Badge>
                    </CardTitle>
                    <span className="text-xs text-muted-foreground">{groups.length} skupin</span>
                  </div>
                </CardHeader>
                {isOpen && (
                  <CardContent className="space-y-2">
                    {groups.map(g => (
                      <div key={`${g.ld_id}-${g.date}`} className="flex items-center justify-between border rounded p-2 text-sm">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium">
                            {g.date || "—"} <Badge variant="outline" className="ml-2">{g.count}</Badge>
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {g.sample_names.join(", ")}{g.count > 3 ? ` … +${g.count - 3}` : ""}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={busy}
                          onClick={() => bulkDelete(g.user_ids, g.ld_id, `${g.date} · ${g.count} članov`)}
                          data-testid={`sa-cleanup-delete-${g.ld_id}-${g.date}`}
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1" />
                          Izbriši
                        </Button>
                      </div>
                    ))}
                  </CardContent>
                )}
              </Card>
            );
          })}
          {Object.keys(byLd).length === 0 && !loading && (
            <Card className="border-dashed"><CardContent className="py-10 text-center text-muted-foreground">
              Ni podatkov.
            </CardContent></Card>
          )}
        </div>
      )}
    </div>
  );
};

export default SuperAdminCleanup;
